<template>
  <div class="po-list">
    <KTCodePreview v-bind:title="'Danh sách Đơn đặt xe'">
      <template v-slot:toolbar>
        <div class="row">
          <div class="ml-3">
            <b-button
              v-if="canCreate"
              size="sm"
              variant="primary"
              @click="goCreate"
            >
              <i style="font-size: 1rem" class="flaticon2-add-1"></i>
              <b>Tạo Đơn đặt xe</b>
            </b-button>
          </div>
          <div class="ml-3" v-if="canViewReport">
            <b-button size="sm" variant="outline-primary" @click="goReport">
              <i style="font-size: 1rem" class="flaticon2-chart"></i>
              Công nợ NPP
            </b-button>
          </div>
        </div>
      </template>

      <template v-slot:preview>
        <!-- Filter -->
        <div class="po-filter-card">
          <b-row>
            <b-col cols="12" md="3">
              <label class="po-label">Nhà phân phối</label>
              <b-form-select
                size="sm"
                v-model="filter.providerId"
                :options="providerOptions"
                class="select-style"
              />
            </b-col>
            <b-col cols="6" md="3">
              <label class="po-label">Trạng thái</label>
              <b-form-select
                size="sm"
                v-model="filter.status"
                :options="statusFilterOptions"
                class="select-style"
              />
            </b-col>
            <b-col cols="6" md="2" class="d-flex align-items-end">
              <b-button block variant="primary" size="sm" @click="onFilter">
                <i class="flaticon2-magnifier-tool"></i>
                Lọc
              </b-button>
            </b-col>
          </b-row>
        </div>

        <!-- Table -->
        <b-table
          :items="items"
          :fields="fields"
          :busy="loading"
          responsive
          bordered
          hover
          show-empty
          empty-text="Không có đơn mua xe nào."
          class="po-table"
        >
          <template #table-busy>
            <div class="text-center text-primary my-3">
              <b-spinner class="align-middle mr-2"></b-spinner>
              <strong>Đang tải...</strong>
            </div>
          </template>

          <template #cell(code)="row">
            <span
              class="po-code mono text-primary font-weight-bold"
              @click="goDetail(row.item)"
            >
              {{ row.item.code || '—' }}
            </span>
          </template>

          <template #cell(providerName)="row">
            {{ providerName(row.item) }}
          </template>

          <template #cell(totalAmount)="row">
            <b class="po-amount">{{ formatPrice(row.item.totalAmount) }}</b>
          </template>

          <template #cell(expectedDeliveryDate)="row">
            {{ formatDateOnly(row.item.expectedDeliveryDate) }}
          </template>

          <template #cell(createdAt)="row">
            {{ formatDate(row.item.createdAt) }}
          </template>

          <template #cell(status)="row">
            <span
              class="po-status-pill"
              :style="statusPillStyle(row.item.status)"
            >
              <i :class="['po-status-icon', statusIcon(row.item.status)]"></i>
              {{ statusLabel(row.item.status) }}
            </span>
          </template>

          <template #cell(actions)="row">
            <div class="d-flex justify-content-center">
              <b-dropdown
                size="sm"
                id="dropdown-left"
                no-caret
                right
                boundary="window"
              >
                <template slot="button-content">
                  <i class="flaticon2-settings function-btn"></i>
                </template>
                <b-dropdown-item @click="goDetail(row.item)">
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon2-search-1"></i>
                    &nbsp; Xem chi tiết
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canEdit(row.item)"
                  @click="goEdit(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i style="font-size: 1rem" class="flaticon2-pen"></i>
                    &nbsp; Sửa
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canApprove(row.item)"
                  @click="onApprove(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #1bc5bd"
                      class="flaticon2-check-mark"
                    ></i>
                    &nbsp; Duyệt
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canShortClose(row.item)"
                  @click="onShortClose(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #ffa800"
                      class="flaticon2-fast-back"
                    ></i>
                    &nbsp; Đóng ngắn
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canCancel(row.item)"
                  @click="onCancel(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #f64e60"
                      class="fas fa-ban"
                    ></i>
                    &nbsp; Hủy đơn
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canDelete(row.item)"
                  @click="onDelete(row.item)"
                >
                  <span style="color: #3f4254; font-size: 12px">
                    <i
                      style="font-size: 1rem; color: #f64e60"
                      class="flaticon2-trash"
                    ></i>
                    &nbsp; Xóa
                  </span>
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row>
          <b-col>
            <p class="mt-3 text-dark" style="font-weight: 500">
              Tổng số: {{ total }}
            </p>
          </b-col>
          <b-col class="d-flex justify-content-end">
            <b-pagination
              v-if="numberOfPage >= 2"
              v-model="page"
              :total-rows="total"
              :per-page="size"
              align="right"
              @change="onPageChange"
            />
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>

    <!-- Modal: Đóng ngắn (lý do) -->
    <b-modal
      v-model="shortCloseDialog.show"
      title="Đóng ngắn Đơn đặt xe"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small mb-3">
        Đóng ngắn sẽ giải phóng công nợ cam kết cho phần xe chưa nhập kho và
        chuyển PO sang trạng thái Hoàn tất.
      </p>
      <b-form-group>
        <label>Lý do (không bắt buộc):</label>
        <b-form-textarea
          v-model="shortCloseDialog.reason"
          size="sm"
          :rows="3"
          :max-rows="5"
          placeholder="Nhập lý do đóng ngắn"
        />
      </b-form-group>
      <b-alert v-if="shortCloseDialog.error" variant="danger" show>
        {{ shortCloseDialog.error }}
      </b-alert>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          :disabled="shortCloseDialog.submitting"
          @click="shortCloseDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="warning"
          :disabled="shortCloseDialog.submitting"
          @click="submitShortClose"
        >
          Xác nhận
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';

import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import PurchaseOrderService from '@/core/services/purchaseOrder.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import {
  PO_STATUS,
  PO_STATUS_LABEL,
  PO_STATUS_ICON,
  PO_STATUS_PILL,
  PO_STATUS_OPTIONS,
} from '@/core/constants/purchaseOrder';
import { checkPermission } from '@/utils/saveDataToLocal';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import Swal from 'sweetalert2';

export default {
  name: 'PurchaseOrderList',
  components: { KTCodePreview },
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      page: parseInt(this.$route.query.page) || 1,
      size: 20,
      filter: {
        providerId: null,
        status: null,
      },
      providerOptions: [{ value: null, text: 'Tất cả nhà phân phối' }],
      fields: [
        { key: 'code', label: 'Mã PO', class: 'align-middle' },
        { key: 'providerName', label: 'Nhà phân phối', class: 'align-middle' },
        {
          key: 'totalAmount',
          label: 'Tổng tiền',
          class: 'align-middle text-right',
        },
        {
          key: 'expectedDeliveryDate',
          label: 'Ngày dự kiến giao',
          class: 'align-middle text-center',
        },
        {
          key: 'createdAt',
          label: 'Ngày tạo',
          class: 'align-middle text-center',
        },
        {
          key: 'status',
          label: 'Trạng thái',
          class: 'align-middle text-center',
        },
        {
          key: 'actions',
          label: '',
          class: 'align-middle text-center',
          thStyle: { width: '60px' },
        },
      ],
      shortCloseDialog: {
        show: false,
        target: null,
        reason: '',
        error: '',
        submitting: false,
      },
    };
  },
  computed: {
    canCreate() {
      return checkPermission('PURCHASE_ORDER_CREATE');
    },
    canViewReport() {
      return checkPermission('REPORT_SUPPLIER_DEBT');
    },
    statusFilterOptions() {
      return [{ value: null, text: 'Tất cả trạng thái' }, ...PO_STATUS_OPTIONS];
    },
    numberOfPage() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
  },
  watch: {
    '$route.query.page'(newPage) {
      this.page = parseInt(newPage) || 1;
      this.loadList();
    },
  },
  created() {
    this.fetchProviders();
    this.loadList();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Đơn đặt xe', route: '/vehicle-purchase-orders' },
      { title: 'Danh sách' },
    ]);
  },
  methods: {
    statusLabel(status) {
      return PO_STATUS_LABEL[status] || status;
    },
    statusIcon(status) {
      return PO_STATUS_ICON[status] || 'fas fa-circle';
    },
    statusPillStyle(status) {
      const info = PO_STATUS_PILL[status] || {};
      return {
        background: info.bg || '#F3F6F9',
        color: info.color || '#80808F',
      };
    },
    providerName(item) {
      return get(item, 'provider.name') || item.providerName || '—';
    },
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    formatDate(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '-';
    },
    formatDateOnly(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '-';
    },
    canEdit(item) {
      return (
        item.status === PO_STATUS.DRAFT &&
        checkPermission('PURCHASE_ORDER_UPDATE')
      );
    },
    canApprove(item) {
      return (
        item.status === PO_STATUS.DRAFT &&
        checkPermission('PURCHASE_ORDER_APPROVE')
      );
    },
    canShortClose(item) {
      return (
        item.status === PO_STATUS.APPROVED &&
        checkPermission('PURCHASE_ORDER_CLOSE')
      );
    },
    canDelete(item) {
      return (
        item.status === PO_STATUS.DRAFT &&
        checkPermission('PURCHASE_ORDER_DELETE')
      );
    },
    canCancel(item) {
      return (
        item.status === PO_STATUS.DRAFT &&
        checkPermission('PURCHASE_ORDER_DELETE')
      );
    },
    onFilter() {
      this.page = 1;
      if (this.$route.query.page && this.$route.query.page !== '1') {
        this.$router.replace({ query: { ...this.$route.query, page: 1 } });
      } else {
        this.loadList();
      }
    },
    onPageChange(pageNum) {
      this.page = pageNum;
      this.$router.replace({ query: { ...this.$route.query, page: pageNum } });
    },
    async fetchProviders() {
      try {
        const { data } = await ApiService.get('providers/getAll');
        // BE shape có thể là { data: [...] } hoặc { data: { data: [...] } }.
        const list = get(data, 'data.data') || get(data, 'data') || [];
        if (Array.isArray(list)) {
          this.providerOptions = [
            { value: null, text: 'Tất cả nhà phân phối' },
            ...list.map((p) => ({
              value: p.id,
              text: p.name || p.shortName || p.id,
            })),
          ];
        }
      } catch (err) {
        console.error('fetchProviders error', err);
      }
    },
    async loadList() {
      this.loading = true;
      try {
        const params = {
          page: this.page,
          limit: this.size,
          status: this.filter.status || undefined,
          providerId: this.filter.providerId || undefined,
        };
        const res = await PurchaseOrderService.getAll(params);
        // BE: { success, data: { data: rows, total, page, limit } }
        const payload = get(res, 'data', {});
        const inner = payload.data;
        if (inner && Array.isArray(inner.data)) {
          this.items = inner.data;
          this.total =
            typeof inner.total === 'number' ? inner.total : inner.data.length;
        } else if (Array.isArray(inner)) {
          this.items = inner;
          this.total = inner.length;
        } else {
          this.items = [];
          this.total = 0;
        }
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được danh sách',
        );
        this.items = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    goCreate() {
      this.$router.push({ name: 'create-purchase-order' });
    },
    goReport() {
      this.$router.push({ name: 'po-supplier-debt-report' });
    },
    goDetail(item) {
      this.$router.push({
        name: 'detail-purchase-order',
        params: { id: item.id },
      });
    },
    goEdit(item) {
      this.$router.push({
        name: 'edit-purchase-order',
        params: { id: item.id },
      });
    },
    async onApprove(item) {
      const result = await Swal.fire({
        title: 'Duyệt đơn đặt xe',
        text: `Duyệt đơn đặt xe ${item.code || ''}? Công nợ cam kết của Nhà phân phối sẽ tăng tương ứng.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1bc5bd',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Duyệt',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      });
      if (!result.value) return;
      try {
        await PurchaseOrderService.approve(item.id);
        makeToastSuccess('Đã duyệt đơn mua xe');
        this.loadList();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') || err.message || 'Duyệt thất bại',
        );
      }
    },
    onShortClose(item) {
      this.shortCloseDialog = {
        show: true,
        target: item,
        reason: '',
        error: '',
        submitting: false,
      };
    },
    async submitShortClose() {
      const item = this.shortCloseDialog.target;
      if (!item) return;
      this.shortCloseDialog.submitting = true;
      this.shortCloseDialog.error = '';
      try {
        await PurchaseOrderService.shortClose(
          item.id,
          (this.shortCloseDialog.reason || '').trim() || undefined,
        );
        makeToastSuccess('Đã đóng ngắn đơn mua xe');
        this.shortCloseDialog.show = false;
        this.loadList();
      } catch (err) {
        this.shortCloseDialog.error =
          get(err, 'response.data.message') ||
          err.message ||
          'Đóng ngắn thất bại';
      } finally {
        this.shortCloseDialog.submitting = false;
      }
    },
    async onDelete(item) {
      const result = await Swal.fire({
        title: 'Xóa đơn đặt xe',
        text: `Bạn có chắc muốn xóa đơn đặt xe ${item.code || ''}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xóa',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      });
      if (!result.value) return;
      try {
        await PurchaseOrderService.remove(item.id);
        makeToastSuccess('Đã xóa đơn mua xe');
        this.loadList();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') || err.message || 'Xóa thất bại',
        );
      }
    },
    async onCancel(item) {
      const result = await Swal.fire({
        title: 'Hủy đơn đặt xe',
        text: `Hủy đơn đặt xe ${item.code || ''}? Đơn sẽ chuyển sang trạng thái "Đã hủy" (vẫn giữ lại để tra cứu).`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Hủy đơn',
        cancelButtonText: '<span style="color:#464E5F">Đóng</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      });
      if (!result.value) return;
      try {
        await PurchaseOrderService.cancel(item.id);
        makeToastSuccess('Đã hủy đơn đặt xe');
        this.loadList();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') || err.message || 'Hủy thất bại',
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$po-primary: #3699ff;
$po-border: #e5eaee;
$po-text-muted: #b5b5c3;
$po-success: #2e7d32;

.po-list {
  .po-filter-card {
    background: #fff;
    border: 1px solid $po-border;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }
  .po-label {
    font-size: 11px;
    color: $po-text-muted;
    margin-bottom: 4px;
    display: block;
    font-weight: 500;
  }

  .po-code {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .mono {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
  .po-amount {
    color: $po-success;
  }
  .icon-color {
    color: #464e5f;
  }
  .po-action-text {
    color: #3f4254;
    font-size: 12px;
  }

  // Status pill (frontend-styling rule #7).
  .po-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 100px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1;
  }
  .po-status-icon {
    font-size: 10px;
    // FA icon đè màu xám bởi Metronic global rule → ép kế thừa (rule #1).
    color: inherit !important;
  }
}
</style>
